import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/ui/components/primitives/dialog";
import { Label } from "@/ui/components/primitives/label";
import { Textarea } from "@/ui/components/primitives/textarea";
import { Button } from "@/ui/components/primitives/button";
import { Badge } from "@/ui/components/primitives/badge";
import { Separator } from "@/ui/components/primitives/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/primitives/select";
import { cn } from "@/ui/components/primitives/utils";
import {
  updatePayout,
  type PayoutApproval,
  type PayoutStatus,
} from "@/services/api/services/admin-payouts";
import { showSuccessToast, showErrorToast } from "@/services/toast-helper";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  payout: PayoutApproval | null;
  onUpdateComplete?: () => void;
};

const PAYOUT_STATUSES: PayoutStatus[] = [
  "PENDING",
  "QUEUED",
  "PROCESSING",
  "SUCCESS",
  "FAILED",
  "CANCELLED",
];

function PayoutStatusBadge({ status }: { status: string }) {
  const statusLower = status.toLowerCase();
  const variants: Record<string, string> = {
    pending: "bg-warning/10 text-warning border-warning/20",
    processing: "bg-primary/10 text-primary border-primary/20",
    queued: "bg-primary/10 text-primary border-primary/20",
    success: "bg-success/10 text-success border-success/20",
    failed: "bg-destructive/10 text-destructive border-destructive/20",
    cancelled: "bg-muted text-muted-foreground border-border",
  };

  return (
    <Badge className={cn(variants[statusLower] || variants.pending)}>
      {status}
    </Badge>
  );
}

export function UpdatePayoutModal({
  open,
  onOpenChange,
  payout,
  onUpdateComplete,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<PayoutStatus>("PENDING");
  const [notes, setNotes] = useState("");
  const [metadata, setMetadata] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [metadataError, setMetadataError] = useState("");

  // Initialize form fields when payout changes
  useEffect(() => {
    if (payout) {
      setStatus((payout.status as PayoutStatus) || "PENDING");
      setNotes(payout.notes || "");
      setMetadata(
        payout.metadata ? JSON.stringify(payout.metadata, null, 2) : ""
      );
      setScheduledAt("");
      setMetadataError("");
    }
  }, [payout]);

  if (!payout) return null;

  const validateMetadata = (value: string): boolean => {
    if (!value.trim()) {
      setMetadataError("");
      return true;
    }
    try {
      JSON.parse(value);
      setMetadataError("");
      return true;
    } catch (e) {
      setMetadataError("Invalid JSON format");
      return false;
    }
  };

  const handleMetadataChange = (value: string) => {
    setMetadata(value);
    validateMetadata(value);
  };

  const handleUpdate = async () => {
    // Validate metadata
    if (!validateMetadata(metadata)) {
      showErrorToast("Please fix the metadata JSON format");
      return;
    }

    // Validate notes length
    if (notes.length > 500) {
      showErrorToast("Notes must be 500 characters or less");
      return;
    }

    setLoading(true);
    try {
      const updateData: any = {};

      // Only include changed fields
      if (status !== payout.status) {
        updateData.status = status;
      }

      if (notes !== (payout.notes || "")) {
        updateData.notes = notes;
      }

      // Parse metadata if present and changed
      if (metadata.trim()) {
        const parsedMetadata = JSON.parse(metadata);
        const currentMetadata = payout.metadata || {};
        if (JSON.stringify(parsedMetadata) !== JSON.stringify(currentMetadata)) {
          updateData.metadata = parsedMetadata;
        }
      }

      // Add scheduled date if present
      if (scheduledAt) {
        updateData.scheduledAt = new Date(scheduledAt).toISOString();
      }

      // Check if anything changed
      if (Object.keys(updateData).length === 0) {
        showErrorToast("No changes detected");
        return;
      }

      await updatePayout(payout.id, updateData);
      showSuccessToast("Payout updated successfully");
      onOpenChange(false);
      onUpdateComplete?.();
    } catch (error: any) {
      const message =
        error?.detail?.error?.message ||
        error?.message ||
        "Failed to update payout";
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Update Payout Details</DialogTitle>
          <DialogDescription>
            Modify payout status, notes, metadata, or schedule
          </DialogDescription>
        </DialogHeader>

        {/* Current Information (Read-Only) */}
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground font-medium">
            Current Information
          </p>
          <div className="bg-muted/30 rounded-md p-3 space-y-2">
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Payout ID</p>
                <p className="font-mono text-xs break-all">{payout.id}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Current Status</p>
                <PayoutStatusBadge status={payout.status} />
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Amount</p>
                <p className="font-medium">
                  {payout.currency === "USDT" ? "$" : "₹"}
                  {payout.amount.toLocaleString()} {payout.currency}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Merchant</p>
                <p>{payout.merchantName || payout.merchant?.name || 'Unknown Merchant'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Beneficiary</p>
                <p>{payout.beneficiaryDetails?.name || payout.beneficiary?.name || 'Unknown Beneficiary'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Created</p>
                <p>
                  {payout.createdAt ? (() => {
                    try {
                      const date = new Date(payout.createdAt);
                      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString();
                    } catch {
                      return 'Invalid Date';
                    }
                  })() : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Editable Fields */}
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground font-medium">
            Edit Details
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            {/* Status Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="status">Payout Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as PayoutStatus)}>
                <SelectTrigger id="status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {PAYOUT_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Scheduled Date */}
            <div className="space-y-2">
              <Label htmlFor="scheduledAt">Scheduled At (Optional)</Label>
              <input
                id="scheduledAt"
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-input-background px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>

          {/* Notes Textarea */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="notes">Notes</Label>
              <span
                className={cn(
                  "text-xs",
                  notes.length > 500
                    ? "text-destructive"
                    : "text-muted-foreground"
                )}
              >
                {notes.length}/500
              </span>
            </div>
            <Textarea
              id="notes"
              placeholder="Add notes about this payout..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className={cn(notes.length > 500 && "border-destructive")}
            />
          </div>

          {/* Metadata Textarea */}
          <div className="space-y-2">
            <Label htmlFor="metadata">Metadata (JSON)</Label>
            <Textarea
              id="metadata"
              placeholder='{"key": "value"}'
              value={metadata}
              onChange={(e) => handleMetadataChange(e.target.value)}
              rows={4}
              className={cn(
                "font-mono text-xs",
                metadataError && "border-destructive"
              )}
            />
            {metadataError && (
              <p className="text-xs text-destructive">{metadataError}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Enter valid JSON or leave empty
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={loading || !!metadataError || notes.length > 500}
          >
            {loading ? "Updating..." : "Update Payout"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
